import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDealerAuth } from '../../context/DealerAuthContext'
import { DealerToastProvider } from '../Toast/DealerToast'
import logoSrc from '../../../assets/logo_with_label.png'
import markSrc from '../../../assets/vfied_mark.png'
import './DealerLayout.css'

const NAV_ITEMS = [
  { to: '/', key: 'dashboard', end: true },
  { to: '/requests', key: 'requests', end: false },
  { to: '/profile', key: 'profile', end: false },
]

export default function DealerLayout() {
  const { me } = useDealerAuth()
  const { t } = useTranslation()

  return (
    <div className="dealer-layout">
      <aside className="dealer-sidebar">
        <div className="dealer-sidebar__brand">
          <img src={logoSrc} alt="Verrafied" className="dealer-sidebar__logo" />
          <img src={markSrc} alt="Verrafied" className="dealer-sidebar__mark" />
        </div>

        <nav className="dealer-sidebar__nav" aria-label={t('dealer.nav.aria_label')}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={t(`dealer.nav.${item.key}`)}
              className={({ isActive }) =>
                `dealer-sidebar__link${isActive ? ' dealer-sidebar__link--active' : ''}`
              }
            >
              <NavIcon name={item.key} />
              <span className="dealer-sidebar__label">{t(`dealer.nav.${item.key}`)}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="dealer-main">
        <header className="dealer-topbar">
          <div className="dealer-topbar__branch" title={me?.branchName}>
            <BranchIcon />
            <span>{me?.branchName}</span>
          </div>
          <UserMenu />
        </header>

        <main className="dealer-content">
          <DealerToastProvider>
            <Outlet />
          </DealerToastProvider>
        </main>
      </div>
    </div>
  )
}

function UserMenu() {
  const { me, logout } = useDealerAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // закрываем меню по клику снаружи и по Escape
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function handleLogout() {
    setOpen(false)
    await logout()
    navigate('/login', { replace: true })
  }

  const fullName = [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || ''
  const initials =
    ((me?.firstName?.[0] ?? '') + (me?.lastName?.[0] ?? '')).toUpperCase() ||
    (me?.email?.[0] ?? '').toUpperCase()

  return (
    <div className="dealer-user" ref={rootRef}>
      <button
        type="button"
        className="dealer-user__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="dealer-user__avatar" aria-hidden="true">{initials}</span>
        <span className="dealer-user__name">{fullName}</span>
        <ChevronIcon />
      </button>

      {open && (
        <div className="dealer-user__menu" role="menu">
          <div className="dealer-user__info">
            <span className="dealer-user__info-name">{fullName}</span>
            {me?.email && <span className="dealer-user__info-line">{me.email}</span>}
            {me?.branchName && <span className="dealer-user__info-line">{me.branchName}</span>}
          </div>
          <button type="button" role="menuitem" className="dealer-user__item" onClick={handleLogout}>
            <LogoutIcon />
            {t('dealer.user_menu.logout')}
          </button>
        </div>
      )}
    </div>
  )
}

function NavIcon({ name }: { name: string }) {
  const icons: Record<string, JSX.Element> = {
    dashboard: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    requests: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
        <path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 5h6M5 8h6M5 11h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    profile: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  }
  return icons[name] ?? null
}

function BranchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 14V6l6-4 6 4v8M6 14v-4h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
